import type { IntellioChartPanel } from '@triedge/ai-assistant-ui';
import type { AdmissionsAssistantArtifactWorkspaceRef } from './types';

export type AssistantChartArtifactResolvedState = {
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  chartPanels: IntellioChartPanel[];
};

export type AssistantChartArtifactDebugSource =
  | 'completed-task-announcement'
  | 'message-inline-hydration'
  | 'chart-workspace';

export type AssistantChartArtifactStateResolver = (
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef,
) => Promise<AssistantChartArtifactResolvedState | null>;

type AssistantChartArtifactStateCacheEntry = {
  inFlight?: Promise<AssistantChartArtifactResolvedState>;
  resolvedState?: AssistantChartArtifactResolvedState;
};

const assistantChartArtifactStateCache = new Map<string, AssistantChartArtifactStateCacheEntry>();

type AssistantChartArtifactDebugEntry = {
  stage:
    | 'cache-hit'
    | 'cache-join'
    | 'uncached-start'
    | 'refresh-start'
    | 'refresh-result'
    | 'download-hydrate-start'
    | 'download-hydrate-fallback-start';
  sourceRef: string | null;
  debugSource?: AssistantChartArtifactDebugSource;
  initialChartsCount: number;
  hasRefreshResolver: boolean;
  chartPanelsCount?: number;
  refreshReturnedChartArtifact?: boolean;
  timestamp: number;
};

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const toChartPanel = (value: unknown): IntellioChartPanel | null => {
  const record = toRecord(value);
  if (!record) {
    return null;
  }
  const kind = typeof record.kind === 'string' ? record.kind : null;
  const xKey = typeof record.xKey === 'string' ? record.xKey : null;
  if (!kind || !xKey) {
    return null;
  }
  const series = Array.isArray(record.series)
    ? record.series
      .map((entry) => {
        const item = toRecord(entry);
        if (!item || typeof item.key !== 'string' || typeof item.label !== 'string') {
          return null;
        }
        return {
          key: item.key,
          label: item.label,
          color: typeof item.color === 'string' ? item.color : '#2697A2',
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
    : [];
  const data = Array.isArray(record.data)
    ? record.data
      .map((entry) => toRecord(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry))
    : [];
  if (series.length === 0 || data.length === 0) {
    return null;
  }
  return {
    id: typeof record.id === 'string' ? record.id : `${kind}-${xKey}`,
    title: typeof record.title === 'string' ? record.title : 'Chart preview',
    subtitle: typeof record.subtitle === 'string' ? record.subtitle : undefined,
    kind: kind as IntellioChartPanel['kind'],
    xKey,
    series,
    data,
  };
};

export const coerceChartPanels = (value: unknown): IntellioChartPanel[] => (
  Array.isArray(value)
    ? value
      .map((entry) => toChartPanel(entry))
      .filter((entry): entry is IntellioChartPanel => Boolean(entry))
    : []
);

const sleep = (ms: number) => new Promise((resolve) => {
  globalThis.setTimeout(resolve, ms);
});

const publishAssistantChartArtifactDebug = (entry: AssistantChartArtifactDebugEntry): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const scopedWindow = window as typeof window & {
    __assistantChartArtifactDebug?: AssistantChartArtifactDebugEntry[];
  };
  const current = Array.isArray(scopedWindow.__assistantChartArtifactDebug)
    ? scopedWindow.__assistantChartArtifactDebug
    : [];
  const next = [...current, entry].slice(-50);
  scopedWindow.__assistantChartArtifactDebug = next;
};

const resolveChartArtifactStateCacheKey = (
  chartArtifact: Pick<AdmissionsAssistantArtifactWorkspaceRef, 'sourceRef'>,
): string | null => {
  const sourceRef = String(chartArtifact.sourceRef ?? '').trim();
  return sourceRef.length > 0 ? sourceRef : null;
};

const mergeResolvedState = (input: {
  resolvedState: AssistantChartArtifactResolvedState;
  initialCharts?: IntellioChartPanel[];
}): AssistantChartArtifactResolvedState => ({
  chartArtifact: input.resolvedState.chartArtifact,
  chartPanels: input.resolvedState.chartPanels.length > 0
    ? input.resolvedState.chartPanels
    : (input.initialCharts ?? []),
});

export const hydrateChartPanelsFromArtifact = async (downloadUrl: string): Promise<IntellioChartPanel[]> => {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await fetch(downloadUrl);
      if (response.ok) {
        const payload = await response.json();
        const panels = coerceChartPanels(toRecord(payload)?.panels);
        if (panels.length > 0) {
          return panels;
        }
      }
    } catch {
      // Retry transient chart artifact hydration failures.
    }
    if (attempt < 2) {
      await sleep(750 * (attempt + 1));
    }
  }
  return [];
};

export const clearAssistantChartArtifactStateCache = (): void => {
  assistantChartArtifactStateCache.clear();
};

export const clearAssistantChartArtifactStateCacheEntry = (
  chartArtifact: Pick<AdmissionsAssistantArtifactWorkspaceRef, 'sourceRef'> | string | null | undefined,
): void => {
  const cacheKey = typeof chartArtifact === 'string'
    ? chartArtifact.trim() || null
    : resolveChartArtifactStateCacheKey(chartArtifact ?? { sourceRef: null });
  if (!cacheKey) {
    return;
  }
  assistantChartArtifactStateCache.delete(cacheKey);
};

const resolveAssistantChartArtifactStateUncached = async (input: {
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialCharts?: IntellioChartPanel[];
  refreshChartArtifactState?: AssistantChartArtifactStateResolver;
  debugSource?: AssistantChartArtifactDebugSource;
}): Promise<AssistantChartArtifactResolvedState> => {
  let resolvedArtifact = input.chartArtifact;
  let chartPanels = input.initialCharts ?? [];
  const sourceRef = resolveChartArtifactStateCacheKey(input.chartArtifact);

  publishAssistantChartArtifactDebug({
    stage: 'uncached-start',
    sourceRef,
    debugSource: input.debugSource,
    initialChartsCount: chartPanels.length,
    hasRefreshResolver: Boolean(input.refreshChartArtifactState),
    timestamp: Date.now(),
  });

  if (chartPanels.length === 0 && input.chartArtifact.sourceRef && input.refreshChartArtifactState) {
    try {
      publishAssistantChartArtifactDebug({
        stage: 'refresh-start',
        sourceRef,
        debugSource: input.debugSource,
        initialChartsCount: chartPanels.length,
        hasRefreshResolver: true,
        timestamp: Date.now(),
      });
      const refreshed = await input.refreshChartArtifactState(input.chartArtifact);
      publishAssistantChartArtifactDebug({
        stage: 'refresh-result',
        sourceRef,
        debugSource: input.debugSource,
        initialChartsCount: chartPanels.length,
        hasRefreshResolver: true,
        refreshReturnedChartArtifact: Boolean(refreshed?.chartArtifact),
        chartPanelsCount: refreshed?.chartPanels?.length ?? 0,
        timestamp: Date.now(),
      });
      if (refreshed?.chartArtifact) {
        resolvedArtifact = refreshed.chartArtifact;
      }
      if (refreshed?.chartPanels?.length) {
        chartPanels = refreshed.chartPanels;
      }
    } catch {
      // Fall back to fetch-based hydration if the refresh contract is unavailable.
    }
  }

  if (chartPanels.length === 0) {
    publishAssistantChartArtifactDebug({
      stage: 'download-hydrate-start',
      sourceRef,
      debugSource: input.debugSource,
      initialChartsCount: input.initialCharts?.length ?? 0,
      hasRefreshResolver: Boolean(input.refreshChartArtifactState),
      timestamp: Date.now(),
    });
    chartPanels = await hydrateChartPanelsFromArtifact(resolvedArtifact.downloadUrl);
  }

  if (
    chartPanels.length === 0
    && resolvedArtifact.downloadUrl !== input.chartArtifact.downloadUrl
  ) {
    publishAssistantChartArtifactDebug({
      stage: 'download-hydrate-fallback-start',
      sourceRef,
      debugSource: input.debugSource,
      initialChartsCount: input.initialCharts?.length ?? 0,
      hasRefreshResolver: Boolean(input.refreshChartArtifactState),
      timestamp: Date.now(),
    });
    chartPanels = await hydrateChartPanelsFromArtifact(input.chartArtifact.downloadUrl);
  }

  return {
    chartArtifact: resolvedArtifact,
    chartPanels,
  };
};

export const resolveAssistantChartArtifactState = async (input: {
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialCharts?: IntellioChartPanel[];
  refreshChartArtifactState?: AssistantChartArtifactStateResolver;
  debugSource?: AssistantChartArtifactDebugSource;
}): Promise<AssistantChartArtifactResolvedState> => {
  const cacheKey = resolveChartArtifactStateCacheKey(input.chartArtifact);
  if (!cacheKey) {
    return resolveAssistantChartArtifactStateUncached(input);
  }

  const cachedEntry = assistantChartArtifactStateCache.get(cacheKey);
  if (cachedEntry?.resolvedState) {
    publishAssistantChartArtifactDebug({
      stage: 'cache-hit',
      sourceRef: cacheKey,
      debugSource: input.debugSource,
      initialChartsCount: input.initialCharts?.length ?? 0,
      hasRefreshResolver: Boolean(input.refreshChartArtifactState),
      chartPanelsCount: cachedEntry.resolvedState.chartPanels.length,
      timestamp: Date.now(),
    });
    return mergeResolvedState({
      resolvedState: cachedEntry.resolvedState,
      initialCharts: input.initialCharts,
    });
  }
  if (cachedEntry?.inFlight) {
    publishAssistantChartArtifactDebug({
      stage: 'cache-join',
      sourceRef: cacheKey,
      debugSource: input.debugSource,
      initialChartsCount: input.initialCharts?.length ?? 0,
      hasRefreshResolver: Boolean(input.refreshChartArtifactState),
      timestamp: Date.now(),
    });
    return cachedEntry.inFlight.then((resolvedState) => {
      const mergedState = mergeResolvedState({
        resolvedState,
        initialCharts: input.initialCharts,
      });
      assistantChartArtifactStateCache.set(cacheKey, { resolvedState: mergedState });
      return mergedState;
    });
  }

  const inFlight = resolveAssistantChartArtifactStateUncached(input)
    .then((resolvedState) => {
      const mergedState = mergeResolvedState({
        resolvedState,
        initialCharts: input.initialCharts,
      });
      assistantChartArtifactStateCache.set(cacheKey, { resolvedState: mergedState });
      return mergedState;
    })
    .catch((error) => {
      const latestEntry = assistantChartArtifactStateCache.get(cacheKey);
      if (latestEntry?.resolvedState) {
        return latestEntry.resolvedState;
      }
      assistantChartArtifactStateCache.delete(cacheKey);
      throw error;
    });

  assistantChartArtifactStateCache.set(cacheKey, { inFlight });
  return inFlight;
};
